/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, March 13th 2022, 10:14:10 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CodeIcon from "@mui/icons-material/Code";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";
import IconButton from "@mui/material/IconButton";
import { Archive as ArchiveIcon, Trash2 as TrashIcon } from "react-feather";
import { UseAPIFormSpec } from "lib/api";
import { TextFormInput, TextAreaFormInput, SwitchFormInput, AutoSaveAPIForm } from "ui-widgets/forms";
import { useListEditorContext } from "lib/providers";
import { FormField, FIELD_TYPES } from "lib/schemas";
import { useCopyTextToClipBoard } from "lib/utils";
import { TabbedNavigator } from "ui-widgets/tabs/TabbedNavigator";
import { FieldTypeSettings } from "./FieldTypeSettings";
import { getFieldTypeWidgets } from "./FieldTypeWidgets";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { useFormFieldsAPI } from "form-builder/services";
import { TranslatableTextFormInput, TranslatableTextAreaInput } from "translation/components";
import { ActionDialog } from "ui-widgets/dialogs/GenericDialogs";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import {
  InputHelpDialogWrapper,
  EmphasisText,
  PopupMenu,
  StyledOSDialog,
  OSActionButton,
  OSAlert,
  HelperText,
} from "ui-widgets";
import { COLORSCHEME } from "theme/styles";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { FieldMany2OnePicker } from "./FieldMany2OnePicker";
import { FieldValidators } from "./FieldValidators";
import { FieldCustomSettings } from "./FieldCustomSettings";
import { FieldTypeFormInput } from "./FieldTypeFormInput";

const TypesWithNoSettings = [
  FIELD_TYPES.SECTION_BREAK,
  FIELD_TYPES.PAGE_TITLE,
  FIELD_TYPES.TITLE,
  FIELD_TYPES.SIGNATURE_PAD,
  FIELD_TYPES.PHONE_NUMBER,
];

interface FieldFormProps {
  fieldData: FormField;
  fieldSaveCallbackFn: UseAPIFormSpec<FormField>["setAPIFormResponse"];
}

export const FieldForm = (props: FieldFormProps) => {
  const { useFormFieldAPIForm } = useFormFieldsAPI();
  const useFormFieldAPIFormReturn = useFormFieldAPIForm(
    props.fieldData?.id,
    props.fieldData,
    props.fieldSaveCallbackFn
  );

  return (
    <AutoSaveAPIForm showFeedbackAlerts={false} useAPIFormReturn={useFormFieldAPIFormReturn} loaderType="linear">
      <FormFieldSettings />
    </AutoSaveAPIForm>
  );
};

const FormFieldSettings = () => {
  const {
    useFormReturn: { watch },
  } = useOSAPIFormContext<FormField>();

  const field_type = watch<keyof FormField>("field_type") as FIELD_TYPES;

  const showLinkedField = !Boolean(watch("entries_form"));

  const fieldHasTypeSettings = !TypesWithNoSettings.includes(field_type);

  const isInput = ![
    FIELD_TYPES.PAGE_TITLE,
    FIELD_TYPES.SECTION_BREAK,
    FIELD_TYPES.TITLE,
    FIELD_TYPES.AUTO_GENERATED_SEQUENCE,
  ].includes(field_type);

  const labelForLabel = isInput ? "Field Label" : "Label";

  return field_type ? (
    <>
      <Stack direction="row" sx={{ ml: 3 }}>
        <div style={{ width: "48%" }}>
          <InputHelpDialogWrapper
            helpTitle="Field Label"
            helpContent={<>This is the label that will appear on the Form View as the form is being filled in.</>}
          >
            <TranslatableTextFormInput label={labelForLabel} schema_field_key="label" />
          </InputHelpDialogWrapper>
        </div>

        <div style={{ width: "48%" }}>
          <InputHelpDialogWrapper
            helpTitle="Label for Saved Data"
            helpContent={
              <>
                This is the label that will appear in any other view that will show this piece of data other than the
                form view e.g. on the Detail View, Report View etc.
              </>
            }
          >
            <TranslatableTextFormInput<FormField> label={"Label for Saved Data"} schema_field_key="display_label" />
          </InputHelpDialogWrapper>
        </div>
        <div style={{ width: "4%" }}>
          <FormFieldMoreActions />
        </div>
      </Stack>
      <Box sx={{ ml: 3, mr: 1 }}>
        <FieldTypeFormInput />
      </Box>
      <TabbedNavigator variant="fullWidth">
        <>General Settings</>
        <GeneralSettings isInput={isInput} showLinkedField={showLinkedField} />
        {isInput && <>Validation Settings</>}
        {isInput && <ValidationSettings />}
        {fieldHasTypeSettings && <>{getFieldTypeWidgets(field_type)[0]} Settings</>}
        {fieldHasTypeSettings && <FieldTypeSettings field_type={field_type} />}
        <>Advanced Settings</>
        <Stack rowGap={2}>
          <CustomHTML5Attributes />
          <FieldLayoutSettings />
          <Divider sx={{ borderColor: COLORSCHEME.secondary, marginTop: 2, marginBottom: 2 }} />
          <TextFormInput<FormField> label={"Field Slug"} schema_field_key="slug" />
          <HelperText>
            The field slug is a unique identifier in this form for this field. You can use this to get data from this
            field in other parts of the form, workflow or other parts of your setup. For as long as your form has no
            submissions with data for this field, the slug is automatically generated and updated based on the field
            label. However, once you change your slug value directly, it will not be automatically updated after that.
            You can change it at any time, even for fields that have already been used in form submissions.
          </HelperText>
        </Stack>
      </TabbedNavigator>
    </>
  ) : null;
};

const GeneralSettings = ({ isInput, showLinkedField }: { isInput: boolean; showLinkedField: boolean }) => (
  <>
    <TranslatableTextAreaInput<FormField> label="Description" schema_field_key="description" />
    <TranslatableTextAreaInput<FormField> label="Help Text" schema_field_key="help_text" />
    {isInput ? <SwitchFormInput<FormField> label="Can users edit this field?" schema_field_key="editable" /> : null}
    {showLinkedField ? (
      <InputHelpDialogWrapper
        helpTitle="Linked Field"
        helpContent={
          <>
            A linked field loads default data from a previous Form Submission. In order for it to work, you must make
            sure you have at least one <EmphasisText>Form Submission Selection</EmphasisText> field where the submission
            form has this field as one of its fields.
          </>
        }
      >
        <FieldMany2OnePicker<FormField> schema_field_key="linked_field" label="Linked Field" showFormInLabel />
      </InputHelpDialogWrapper>
    ) : null}
  </>
);

const CustomHTML5Attributes = () => {
  const {
    useFormReturn: { watch, setValue },
  } = useOSAPIFormContext<FormField>();
  const extra_configurations = watch("extra_configurations");
  const custom_html5_attributes = extra_configurations?.custom_html5_attributes ?? {};
  const [errorMessages, setErrorMessages] = React.useState<{ [key: string]: string }>({});

  const onChangeAttributes = (newAttributes: { [key: string]: string }) => {
    setValue(
      "extra_configurations",
      {
        ...extra_configurations,
        custom_html5_attributes: newAttributes,
      },
      { shouldDirty: true }
    );
  };

  const onAddNewAttribute = () => {
    const newIndex = Object.keys(custom_html5_attributes).length;
    onChangeAttributes({
      ...custom_html5_attributes,
      [`data-new-${newIndex}`]: "",
    });
  };

  const onBlurAttributeName = (oldName: string, newName: string) => {
    if (oldName === newName) return;

    if (!newName.startsWith("data-")) {
      setErrorMessages((prev) => ({
        ...prev,
        [oldName]: "The html5 attribute name must start with 'data-'. This attribute will not be saved.",
      }));
      return;
    }

    const attr_value = custom_html5_attributes[oldName];
    delete custom_html5_attributes[oldName];
    onChangeAttributes({
      ...custom_html5_attributes,
      [newName]: attr_value,
    });
  };

  const onBlurAttributeValue = (attrName: string, newValue: string) => {
    if (errorMessages[attrName]) return;

    if (newValue === custom_html5_attributes[attrName]) return;

    onChangeAttributes({
      ...custom_html5_attributes,
      [attrName]: newValue,
    });
  };

  const onDeleteAttribute = (attrName: string) => {
    const newCustomHTML5Attributes = { ...custom_html5_attributes };
    delete newCustomHTML5Attributes[attrName];
    onChangeAttributes(newCustomHTML5Attributes);
  };

  return (
    <StyledOSDialog
      popUpTitle="Custom HTML5 Attributes"
      popUpIcon={<CodeIcon color="primary" />}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton onClick={handleOpen} fullWidth actionStyle="configure" size="large">
          Manage Custom HTML5 Attributes
        </OSActionButton>
      )}
    >
      <Stack direction="column" rowGap="20px">
        {Object.entries(custom_html5_attributes).map(([key, value], index) => {
          return (
            <React.Fragment key={key}>
              <Stack direction="row" columnGap="10px">
                <TextField
                  label="Attribute Name"
                  defaultValue={key}
                  style={{ flexBasis: "50%" }}
                  onBlur={(e) => onBlurAttributeName(key, e.target.value)}
                />
                <TextField
                  label="Attribute Value"
                  defaultValue={value}
                  style={{ flexBasis: "50%" }}
                  onBlur={(e) => onBlurAttributeValue(key, e.target.value)}
                />
                <IconButton onClick={() => onDeleteAttribute(key)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Stack>
              {errorMessages[key] ? <OSAlert severity="error">{errorMessages[key]}</OSAlert> : null}
            </React.Fragment>
          );
        })}
        <Stack direction="row">
          <OSActionButton
            actionStyle="add"
            buttonType="floating"
            size="large"
            onClick={onAddNewAttribute}
            sx={{ ml: "auto" }}
          />
        </Stack>
      </Stack>
    </StyledOSDialog>
  );
};

type TFieldLayoutSettings = {
  widget_grid_size: number;
  input_grid_size: number;
  label_grid_size: number;
};

const FieldLayoutSettings = () => {
  const {
    useFormReturn: { watch, setValue },
  } = useOSAPIFormContext<FormField>();
  const extra_configurations = watch("extra_configurations");
  const fieldLabel = watch("label");
  const layout_settings: TFieldLayoutSettings = extra_configurations?.layout ?? {};
  const [errorMessages, setErrorMessages] = React.useState<{ [k in keyof TFieldLayoutSettings]: string }>(null);

  const onChangeLayoutSettings = (newSettings: TFieldLayoutSettings) => {
    if (!Object.values(newSettings).every((value) => typeof value === "number" && value > 0 && value <= 12)) {
      const message = "Grid size must be a number between 1 and 12";
      if (
        !(
          typeof newSettings.widget_grid_size === "number" &&
          newSettings.widget_grid_size > 0 &&
          newSettings.widget_grid_size <= 12
        )
      ) {
        setErrorMessages((prev) => ({ ...prev, widget_grid_size: message }));
      }
      if (
        !(
          typeof newSettings.input_grid_size === "number" &&
          newSettings.input_grid_size > 0 &&
          newSettings.input_grid_size <= 12
        )
      ) {
        setErrorMessages((prev) => ({ ...prev, input_grid_size: message }));
      }
      if (
        !(
          typeof newSettings.label_grid_size === "number" &&
          newSettings.label_grid_size > 0 &&
          newSettings.label_grid_size <= 12
        )
      ) {
        setErrorMessages((prev) => ({ ...prev, label_grid_size: message }));
      }
      return;
    }

    setErrorMessages(null);

    setValue(
      "extra_configurations",
      {
        ...extra_configurations,
        layout: newSettings,
      },
      { shouldDirty: true }
    );
  };

  return (
    <StyledOSDialog
      popUpTitle={<> Managing Field Layout for {fieldLabel}</>}
      popUpIcon={<ViewComfyIcon color="primary" />}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton onClick={handleOpen} fullWidth actionStyle="custom" customIcon={<ViewComfyIcon />} size="large">
          Manage Field Layout
        </OSActionButton>
      )}
    >
      <Stack direction="column" rowGap="20px">
        <TextField
          label="Widget Grid Size"
          defaultValue={layout_settings.widget_grid_size}
          type="number"
          onBlur={(e) => onChangeLayoutSettings({ ...layout_settings, widget_grid_size: parseInt(e.target.value) })}
        />
        {errorMessages?.widget_grid_size && <OSAlert severity="error">{errorMessages.widget_grid_size}</OSAlert>}
        <TextField
          label="Input Grid Size"
          defaultValue={layout_settings.input_grid_size}
          type="number"
          onBlur={(e) => onChangeLayoutSettings({ ...layout_settings, input_grid_size: parseInt(e.target.value) })}
        />
        {errorMessages?.input_grid_size && <OSAlert severity="error">{errorMessages.input_grid_size}</OSAlert>}
        <TextField
          label="Label Grid Size"
          defaultValue={layout_settings.label_grid_size}
          type="number"
          onBlur={(e) => onChangeLayoutSettings({ ...layout_settings, label_grid_size: parseInt(e.target.value) })}
        />
        {errorMessages?.label_grid_size && <OSAlert severity="error">{errorMessages.label_grid_size}</OSAlert>}
      </Stack>
    </StyledOSDialog>
  );
};

const ValidationSettings = () => {
  const { useFormReturn } = useOSAPIFormContext<FormField>();

  const watched_unique_key = useFormReturn.watch("unique_key");

  return (
    <Grid container>
      <Grid item xs={6}>
        <SwitchFormInput<FormField> label="Required" schema_field_key="required" />
      </Grid>
      <Grid item xs={6}>
        <SwitchFormInput<FormField> label="Required for Draft" schema_field_key="required_for_draft" />
      </Grid>
      <Grid item xs={6}>
        <SwitchFormInput<FormField> label="Unique" schema_field_key="unique_key" />
      </Grid>
      {watched_unique_key && (
        <Grid item xs={12}>
          <TextAreaFormInput<FormField>
            label="Custom error message for a unique validation error"
            schema_field_key="unique_error_message"
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <FieldValidators />
      </Grid>
    </Grid>
  );
};

const FormFieldMoreActions = () => {
  const { useFormReturn, isSuccessfulAPIDataSend } = useOSAPIFormContext<FormField>();

  const { setValue, watch } = useFormReturn;

  const [fieldId, fieldIsVisible, fieldSlug, fieldTag, fieldLabel] = watch<(keyof FormField)[]>([
    "id",
    "visible",
    "slug",
    "artifact_tag",
    "label",
  ]) as [FormField["id"], FormField["visible"], FormField["slug"], FormField["artifact_tag"], FormField["label"]];

  const { removeItem, updateList } = useListEditorContext<FormField>();

  const archiveField = () => {
    setValue("visible", false, { shouldDirty: true });
  };

  React.useEffect(() => {
    if (isSuccessfulAPIDataSend && !fieldIsVisible) {
      removeItem(fieldId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessfulAPIDataSend, fieldId, fieldIsVisible]);

  const {
    handleClose: closeArchiveConfirmation,
    handleOpen: openArchiveConfirmation,
    open: archiveConfirmationOpenState,
  } = useToggleState();

  const {
    handleClose: handleCloseCustomSettings,
    handleOpen: handleOpenCustomSettings,
    open: customSettingsOpenState,
  } = useToggleState();

  const { useDelete } = useFormFieldsAPI();

  const { copied: fieldTagCopied, copyTextToClipBoard: copyFieldTag } = useCopyTextToClipBoard();
  const { copied: fieldSlugCopied, copyTextToClipBoard: copyFieldSlug } = useCopyTextToClipBoard();

  return (
    <>
      <DeleteOSItemDialog<FormField>
        itemId={fieldId}
        useDeleteOSItem={useDelete}
        onDelete={() => removeItem(fieldId)}
        onRestore={(restoredField) => updateList(restoredField)}
      >
        {({ handleOpen: handleOpenDeleteField }) => (
          <>
            <PopupMenu
              menuItems={[
                { icon: <ArchiveIcon size="16" />, onClick: openArchiveConfirmation, text: "Archive Field" },
                { icon: <TrashIcon size="16" />, onClick: handleOpenDeleteField, text: "Delete Field" },
                {
                  icon: (
                    <Tooltip title="Copy Field Tag">
                      <ContentCopyIcon fontSize="small" />
                    </Tooltip>
                  ),
                  onClick: () => copyFieldTag(fieldTag),
                  text: "Copy Field Tag",
                },
                {
                  icon: (
                    <Tooltip title="Copy Field Slug">
                      <ContentCopyIcon fontSize="small" />
                    </Tooltip>
                  ),
                  onClick: () => copyFieldSlug(fieldSlug),
                  text: "Copy Field Slug",
                },
                { icon: <CodeIcon />, onClick: handleOpenCustomSettings, text: `Custom Settings for ${fieldLabel}` },
              ]}
            />
            <Snackbar open={fieldSlugCopied} autoHideDuration={6000} message="Field Slug Copied" />
            <Snackbar open={fieldTagCopied} autoHideDuration={6000} message="Field Tag Copied" />
            <ActionDialog
              severity="warning"
              title="Archive Field"
              open={archiveConfirmationOpenState}
              onClose={closeArchiveConfirmation}
              confirmationAction={() => {
                archiveField();
                closeArchiveConfirmation();
              }}
              dialogMessage={
                <>
                  <p>Archiving a field will no longer make it available for configuration or on your form.</p>
                  <p>Are you sure about this?</p>
                </>
              }
            />
          </>
        )}
      </DeleteOSItemDialog>
      <FieldCustomSettings onClose={handleCloseCustomSettings} open={customSettingsOpenState} />
    </>
  );
};
